import loadable from '@loadable/component';
import BpkBreadcrumb, {
  BpkBreadcrumbItem,
} from '@skyscanner-internal/backpack-web/bpk-component-breadcrumb';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';

import logMiniEventHelper from '../../mini-event/logMiniEventHelper';
import withErrorBoundary from '../ErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  BreadcrumbsProps,
  BreadcrumbsItemLink,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/components';

import STYLES from './Breadcrumbs.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const Breadcrumbs = ({ breadcrumbs: entries, metadata }: BreadcrumbsProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const createBpkBreadcrumbItem = (
    { label, page_type_id: pageTypeId, url }: BreadcrumbsItemLink,
    index: number,
    array: BreadcrumbsItemLink[],
  ) => {
    if (index === array.length - 1) {
      return (
        <BpkBreadcrumbItem
          key={pageTypeId}
          data-tracking-element-id={`${rawComponentName}_item_${index}`}
          data-tracking-common-params={buildMBDParam(
            `${rawComponentName}_item`,
            { trigger_item_label: label },
          )}
          active
        >
          {label}
        </BpkBreadcrumbItem>
      );
    }
    return (
      <BpkBreadcrumbItem
        key={pageTypeId}
        data-tracking-element-id={`${rawComponentName}_item_${index}`}
        data-tracking-common-params={buildMBDParam(`${rawComponentName}_item`, {
          trigger_item_label: label,
        })}
        href={url}
      >
        {label}
      </BpkBreadcrumbItem>
    );
  };

  let links: JSX.Element[] | string = '';
  if (Array.isArray(entries)) links = entries.map(createBpkBreadcrumbItem);

  const schema = { __html: metadata };

  /* eslint-disable react/no-danger */
  return (
    <div
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
      id="breadcrumbs-root"
      className={STYLES.Breadcrumbs}
    >
      <script type="application/ld+json" dangerouslySetInnerHTML={schema} />
      <BpkBreadcrumb label="breadcrumb">{links}</BpkBreadcrumb>
    </div>
  );
  /* eslint-enable react/no-danger */
};

const BreadcrumbsWrapper = (props: BreadcrumbsProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.BREADCRUMBS,
      })
    }
  >
    <Breadcrumbs {...props} />
  </IntersectionObserverWrapper>
);

const BreadcrumbsWithErrorBoundary = withErrorBoundary(
  BreadcrumbsWrapper,
  'breadcrumbs-component',
);

export default BreadcrumbsWithErrorBoundary;
